/// <reference path="../../../../node_modules/@1js/office-online-strings/en-US/transcription-strings.d.ts" />

import * as React from 'react';
import { AppAudioPlayer, AppButton, AppTranscriptCard, IAudioPlayer, ITranscriptData, AppTranscriptCardChangeParameter, AppAudioPlayerFeatureName, getSecondaryButtonOverrideStyles, getAppButtonStyles } from '../../components';
import { getTranscribingPageStyles, getStartOverButtonCustomStyles, getExplainedLabelStyles, getButtonContainerStackTokens, getAppAddToDocumentMenuButtonCustomStyles, getContextualMenuItemStyles, getCalloutItemStyles } from '../../common/pageStyles/TranscribingPage.styles';
import { RouteComponentProps } from 'react-router-dom';
import { Stack, ICalloutProps, Label, Dialog, DialogFooter, IStyleFunctionOrObject, IDialogStyleProps, IDialogStyles, IContextualMenuProps, Spinner, SpinnerType } from 'office-ui-fabric-react';
import type { IDialogProps, IDialogFooterProps, IButton } from 'office-ui-fabric-react';
import { SetEditingMode, SetHighlightedIndex, SetAudioPlaying, SetShowStartOverConfirmDialog, SetTeachingUIClosedFlag, SetFocusedIndex, SetAddToDocumentInProgressStatus } from '../../common/actions/TranscribingActions';
import { StartOverClicked, TranscriptionDataModified, InitializeTranscriptionData, AddTranscriptionToDocument, AddTranscriptionSegmentToDocument } from '../../actions/TranscribingActions'
import transcribingStore from '../../stores/TranscribingStore';
import { INDEX_NONE } from '../../stores/TranscribingStore';
import { ChangeEvent, RequestEvent } from '../../stores/utils';
import { LogEvents } from '../../../utils/LogEvents';
import { TranscriptKeys } from '../../common/telemetry/TranscriptKeys';
import { StartOverKeys } from '../../common/telemetry/StartOverKeys';
import { DurationMeasureType } from '../../../backend/interfaces/ILogEvent';
import { RenderToPaneKeys } from '../../common/telemetry/RenderToPaneKeys';
import { ITranscriptionManager, ITranscription, ITranscriptionSegment } from '../../../backend/interfaces/ITranscriptionManager';
import { AppExplainedLabel } from '../../components/AppExplainedLabel/AppExplainedLabel';
import { ITranscriptionSourceFile, ITranscriptionSourceFileProvider } from '../../../backend/interfaces/ITranscriptionSourceFileProvider';
import { AppTeachingBubble, getTeachingbubbleStyles } from '../../components/AppTeachingBubble';
import { getDialogContentProps, getDialogStyles, getModalProps, getTopLabelStyles, getDialogStylesZoomedOut } from '../../common/styles/Popup.styles';
import { AppLayoutSettings } from '../../common/styles/common.styles';
import { AudioBackupAndRealTimeTranscriptionConnection } from '../../common/connections/AudioBackupAndRealTimeTranscriptionConnection';
import { PlaybackPayload, TranscriptionNotifyEvent } from '../../../backend/interfaces/IEventListener';
import { AddToDocumentMenuOption, TranscribingActionTypes } from '../../common/enums/transcribingEnums';
import { getHostAppInfoBar } from '../../components/HostAppInfoBar/HostAppInfoBar';
import { HostAppUtil } from '../../../backend/office-js/HostAppUtil';
import { HostAppName } from '../../../backend/office-js/HostAppSettings';
import { AddToDocumentResult, EMPTY_STRING, MAX_WAIT_TIME_FOR_ADD_TO_DOCUMENT_REQUEST, TranscribingEntryPoints } from '../../../AppConstants';
import { TranscriptionAlert } from '../../components/Popups/TranscriptionAlert';
import { GetNetworkConnectionMonitorInstance, InternetState } from '../../NetworkConnectionMonitor';
import { IsConnectedToNetwork, NetworkConnectionListener } from '../../NetworkConnectionMonitor';
import { ErrorStringFromException, isNullOrUndefined } from '../../../utils/utils';
import { CreateTranscriptionSourceFileProvider } from '../../../backend/factory/TranscriptionSourceFileFactory';
import { TranscriptionManagerFactory } from '../../../backend/factory/TranscriptionManagerFactory';
import dispatcher from '../../dispatcher';
import { AppAuthenticationProviderContext } from '../../context';
import { BroadcastService } from '../../../backend/manager/eventManager/BroadcastService';
import { FileUploadKeys } from '../../../backend/telemetry/FileUploadKeys';
import { getSpinnerStyles } from '../../common/pageStyles/RecoveringPage.styles';
import { LogEventNames } from '../../../backend/interfaces/ITelemetryLogger';
import { LogError } from '../../../utils/ErrorTelemetry';

const DialogWithChildren = Dialog as React.FunctionComponent<React.PropsWithChildren<IDialogProps>>;
const DialogFooterWithChildren = DialogFooter as React.FunctionComponent<React.PropsWithChildren<IDialogFooterProps>>

export interface TranscribingState {
  /**
   * Track the index of the card which is currently being edited
   */
  editingIndex: number;

  /**
   * Track the index of the card which is currently focused
   */
  focusedIndex: number;

  /**
   * Track the index of the card which is currently highlighted
   */
  highlightedIndex: number;

  /**
   * Source audio file
   */
  sourceFile: ITranscriptionSourceFile | null;

  /**
   * Data for the Transcript cards
   */
  transcriptionData: ITranscriptData[];

  /**
   * Flag to check if audio is playing
   */
  isAudioPlaying: boolean;

  /**
   * Manager for the transcription
   */
  transcriptionManager: ITranscriptionManager | null;

  /**
   * Flag to check if Teaching UI is closed by user
   */
  isTeachingUIClosed: boolean;

  /** Shows a modal dialog warning to the user before starting a new transcript
  */
  showStartOverConfirmDialog: boolean;

  /*Flag to check if audio player is zoomed out of screen i.e height of audio player > inner height */
  isAudioPlayerZoomedOutOfScreen: boolean;

  /* Flag to check if dialog is zoomed out of screen 22+190+player height > inner height*/
  isDialogZoomedOutOfScreen: boolean;

  /* get the transcription entry point*/
  transcribingEntryPoints: TranscribingEntryPoints;

  /* status updated on addToDocument click event*/
  isAddToDocumentInProgress: boolean;
}

export class Transcribing extends React.PureComponent<RouteComponentProps<{}, {}, AudioBackupAndRealTimeTranscriptionConnection>, TranscribingState>
implements NetworkConnectionListener {

  declare context: React.ContextType<typeof AppAuthenticationProviderContext>
  static contextType = AppAuthenticationProviderContext;
  private audioPlayerRef = React.createRef<IAudioPlayer>();
  private addToDocumentButtonRef = React.createRef<IButton>();
  private audioPlayerContainerRef = React.createRef<HTMLDivElement>();
  private _isMounted: boolean;
  private audioDuration: null | number = null;

  /*transcribing page title size 22 which is fixed across all zoom levels */
  static transcribingPageTitleHeight = 22;
  static dialogHeight = 190; // rendred at 100% or default zoom

  constructor(props: RouteComponentProps<{}, {}, AudioBackupAndRealTimeTranscriptionConnection>) {
    super(props);
    this._isMounted = false;

    this.state = transcribingStore.getState();
    transcribingStore.on(ChangeEvent.Change, this._onStoreChanged);
    transcribingStore.on(RequestEvent.CurrentTranscriptionState, this._notifyCurrentState);
  }

  private handleKeyDown(e: any) {
    const { focusedIndex, transcriptionData } = this.state;
    // arrow up/down button should select next/previous list element
    if (e.keyCode === 38 && focusedIndex > 0) { // up arrow pressed
      this.setState(prevState => ({
        focusedIndex: prevState.focusedIndex - 1
      }))
    } else if (e.keyCode === 40 && focusedIndex < transcriptionData.length - 1) { // down arrow pressed
      this.setState(prevState => ({
        focusedIndex: prevState.focusedIndex + 1
      }))
    }
  }

  /*
    The audio player is made a part of scroll bar only when its height becomes more than the inner height of the pane
  */
  private isAudioPlayerZoomedOutOfScreen() {
    const playerContainer = this.audioPlayerContainerRef.current;
    const playerContainerRect = playerContainer?.getBoundingClientRect();

    let isAudioPlayerZoomedOutOfScreen = false;
    if (playerContainerRect?.height != undefined && window.innerHeight < playerContainerRect?.height + Transcribing.transcribingPageTitleHeight) {
      isAudioPlayerZoomedOutOfScreen = true;
    }
    return isAudioPlayerZoomedOutOfScreen;
  }

  private isDialogZoomedOutOfScreen() {
    const playerContainer = this.audioPlayerContainerRef.current;
    const playerContainerRect = playerContainer?.getBoundingClientRect();

    let isDialogZoomedOutOfScreen = false;

    if (playerContainerRect?.height != undefined && window.innerHeight < playerContainerRect?.height + Transcribing.transcribingPageTitleHeight + Transcribing.dialogHeight) {
      isDialogZoomedOutOfScreen = true;
    }
    return isDialogZoomedOutOfScreen;
  }

  private onWindowResize = () => {
    const isAudioPlayerZoomedOutOfScreen = this.isAudioPlayerZoomedOutOfScreen();
    const isDialogZoomedOutOfScreen = this.isDialogZoomedOutOfScreen();
    this.setState({ isAudioPlayerZoomedOutOfScreen: isAudioPlayerZoomedOutOfScreen, isDialogZoomedOutOfScreen: isDialogZoomedOutOfScreen });
  };

  public componentDidMount() {
    this._isMounted = true;
    if (!isNullOrUndefined(this.addToDocumentButtonRef) && !isNullOrUndefined(this.addToDocumentButtonRef.current) )
    {
      this.addToDocumentButtonRef.current!.focus();
    }
    LogEvents.Transcript.Start();
    LogEvents.Transcript.BeginDurationMeasure(DurationMeasureType.Normal, TranscriptKeys.DurationInPane);
    window.addEventListener('resize', this.onWindowResize);
    InitializeTranscriptionData(this.props.history);
    GetNetworkConnectionMonitorInstance().addListener(this);
  }

  public componentWillUnmount() {
    LogEvents.Transcript.End();
    this._isMounted = false;
    window.removeEventListener('resize', this.onWindowResize);
    this._notifyCurrentState();
    GetNetworkConnectionMonitorInstance().removeListener(this);
  }

  public componentDidUpdate() {

    // notify audio state on every render as Audio player state may get changed due to various controling factors like internet connectivity.
    this._notifyCurrentState();

    LogEvents.RenderToPane.End([
      { key: RenderToPaneKeys.RenderedSuccessfully, value: true },
      { key: RenderToPaneKeys.IsShowingAudioFile, value: this.state.sourceFile != null },
      { key: RenderToPaneKeys.IsTranscriptEmpty, value: this.state.transcriptionData.length === 0 },
    ]);
  }

  private shouldShowAudioPlayer(): boolean {
    return (!isNullOrUndefined(this.state.sourceFile) && IsConnectedToNetwork());
  }

  private getAudioPlayerElement(sourceFile: ITranscriptionSourceFile | null, isAudioPlayerZoomedOutOfScreen: boolean) {
    const transcribingStyles = getTranscribingPageStyles(this.shouldShowAudioPlayer(), isAudioPlayerZoomedOutOfScreen);

    if (!IsConnectedToNetwork()) {
      return (
        <div className={transcribingStyles.noAudioFileContainer}>
          <AppExplainedLabel label={TranscriptionStrings.UnableToOpenAudioFileLabel} labelStyles={getExplainedLabelStyles()} iconName="Error" explanation={TranscriptionStrings.InternetNotAvailableErrorMessage} />
        </div>
      );
    }

    if (sourceFile == null) {
      return (
        <div className={transcribingStyles.noAudioFileContainer}>
          <AppExplainedLabel label={TranscriptionStrings.UnableToOpenAudioFileLabel} labelStyles={getExplainedLabelStyles()} iconName="Error" explanation={TranscriptionStrings.UnableToOpenAudioFileExplanation} />
        </div>
      );
    }

    return (
      <div className={transcribingStyles.playerContainer} ref={this.audioPlayerContainerRef}>
        <AppAudioPlayer
          type={'AppAudioPlayerProps'}
          audioSrc={sourceFile.audioUrl}
          audioPlayerTitle={sourceFile.fileName}
          audioPlayerTitleLink={sourceFile.permanentAudioUrl}
          componentRef={this.audioPlayerRef}
          onAudioControlLoaded={this._onAudioControlLoaded}
          onAudioPositionUpdated={this._setHighlightedCard}
          onUserInteraction={this._onAudioPlayerUserInteraction}
          />
      </div>
    );
  }

  public render() {
    const { sourceFile, editingIndex, focusedIndex, highlightedIndex, transcriptionData, isTeachingUIClosed, isAddToDocumentInProgress} = this.state;
    const isAudioPlayerZoomedOutOfScreen = this.isAudioPlayerZoomedOutOfScreen();
    const isDialogZoomedOutOfScreen = this.isDialogZoomedOutOfScreen();
    const transcribingStyles = getTranscribingPageStyles(this.shouldShowAudioPlayer(), isAudioPlayerZoomedOutOfScreen);

    const numOfSegments = transcriptionData.length;
    return (
      <><div> { getHostAppInfoBar(14, 14) } </div>
      <Stack>
        {this.getAudioPlayerElement(sourceFile, isAudioPlayerZoomedOutOfScreen)}
        <div className={transcribingStyles.cardsContainer} data-testid="transcript-items" role="list" onKeyDown={this.handleKeyDown.bind(this)}>

          {
            transcriptionData.map((transcriptionData, index) => {
              return <AppTranscriptCard
                key={index}
                ariaSetSize={numOfSegments}
                ariaPosInSet={index+1}
                type={'AppTranscriptCardProps'}
                transcriptionData={transcriptionData}
                onTimestampClicked={sourceFile != null ? this._onTimestampClicked : null}
                onTranscriptChange={this._onTranscriptionDataChanged}
                isEditing={!isAddToDocumentInProgress && editingIndex === index}
                onEditing={this._onEditingClicked}
                onEditingModeExit={this._onEditingModeExit}
                isFocused={!isAddToDocumentInProgress && index === focusedIndex}
                onFocused={this._onFocusChanged}
                isHighlighted={index === highlightedIndex}
                isDisabled={isAddToDocumentInProgress}
                shouldScrollToHighlightedIndex={this.shouldScrollToHighlightedIndex()}
                addTranscriptionSegmentToDocument={this._onAddTranscriptionSegmentToDocument} />
            })
          }
        </div>

        {!isTeachingUIClosed && (this.getTeachingUIElement())}
        {isAddToDocumentInProgress ? <div className={transcribingStyles.spinnerContainer}>
          <Spinner type={SpinnerType.large} styles={getSpinnerStyles()} />
        </div> : null}
        <div className={transcribingStyles.buttonContainer}>
          <Stack horizontal horizontalAlign="center" tokens={getButtonContainerStackTokens()}>
            <AppButton
              type={'AppButtonProps'}
              styles={getAppAddToDocumentMenuButtonCustomStyles()}
              id={'addToDocument'}
              buttonRef={this.addToDocumentButtonRef}
              disabled={isAddToDocumentInProgress}
              text={this.getAddTranscriptionButtonLabel()}
              menuProps={this.getAddToDocumentMenuProps()} />
            <AppButton
              type={'AppButtonProps'}
              styles={getStartOverButtonCustomStyles()}
              id={'startOver'}
              disabled={isAddToDocumentInProgress}
              text={TranscriptionStrings.StartNewTranscriptionButtonLabel}
              onExecute={this._onStartOverClicked} />
          </Stack>
        </div>
        <TranscriptionAlert/>
        {
          this.state.showStartOverConfirmDialog ? this._showConfirmNewTranscriptDialog(isDialogZoomedOutOfScreen ? getDialogStylesZoomedOut() : getDialogStyles()): null
        }
      </Stack></>
    );
  }

  private getTeachingUIElement() {
    const getCalloutProps = (): Partial<ICalloutProps> => {
      const props: Partial<ICalloutProps> = {
        isBeakVisible: false,
        gapSpace: 12,
        preventDismissOnLostFocus: true,
        preventDismissOnScroll: true,
        preventDismissOnResize: true
      }
      return props;
    }

    const _onTeachingUICloseClicked = (): void => {
      SetTeachingUIClosedFlag(true);
    };

    return (
      <AppTeachingBubble
        target='#addToDocument'
        focusTrapZoneProps={{ disabled: true }}
        calloutProps={getCalloutProps()}
        styles={getTeachingbubbleStyles()}
        hasCloseIcon={true}
        text={TranscriptionStrings.TranscribingPageTeachingUIMessage}
        onDismiss={_onTeachingUICloseClicked}
        closeButtonAriaLabel={TranscriptionStrings.CloseLabel}
      />
    );
  }

  private getAddToDocumentMenuProps() {
    const menuProps: IContextualMenuProps = {
      items: [
        {
          key: AddToDocumentMenuOption.JustText,
          name: TranscriptionStrings.AddTranscriptionToDocumentButtonOptionJustText,
          style: getContextualMenuItemStyles(),
          onClick: () => { this._onAddTranscriptionToDocumentClicked(AddToDocumentMenuOption.JustText) },
          target: '_blank',
        },
        {
          key: AddToDocumentMenuOption.WithSpeakers,
          name: TranscriptionStrings.AddTranscriptionToDocumentButtonOptionWithSpeakers,
          style: getContextualMenuItemStyles(),
          onClick: () => { this._onAddTranscriptionToDocumentClicked(AddToDocumentMenuOption.WithSpeakers) },
          target: '_blank',
        },
        {
          key: AddToDocumentMenuOption.WithTimestamps,
          name: TranscriptionStrings.AddTranscriptionToDocumentButtonOptionWithTimestamps,
          style: getContextualMenuItemStyles(),
          onClick: () => { this._onAddTranscriptionToDocumentClicked(AddToDocumentMenuOption.WithTimestamps) },
          target: '_blank',
        },
        {
          key: AddToDocumentMenuOption.WithSpeakersAndTimestamps,
          name: TranscriptionStrings.AddTranscriptionToDocumentButtonOptionWithSpeakersAndTimestamps,
          style: getContextualMenuItemStyles(),
          onClick: () => { this._onAddTranscriptionToDocumentClicked(AddToDocumentMenuOption.WithSpeakersAndTimestamps) },
          target: '_blank',
        },
      ],
      styles: {
        subComponentStyles: {
          callout: getCalloutItemStyles()
        }
      }
    };
    return menuProps;
  }

  private getConfirmNewTranscriptDialogMessage() {
    switch (HostAppUtil.GetHostAppName()) {
      case HostAppName.WORD:
        return TranscriptionStrings.ConfirmNewTranscriptionDialogMessage;
      case HostAppName.ONENOTE:
        return TranscriptionStrings.ConfirmNewTranscriptionDialogMessageOneNote;
      default:
        return EMPTY_STRING;
    }
  }

  private _showConfirmNewTranscriptDialog(styles: IStyleFunctionOrObject<IDialogStyleProps, IDialogStyles>) {
    // VSO:4379118 tracks removing hard-coded string below
    // and in the OK confirmation on dialog below
    const confirmNewTranscriptDialogTitle: string | JSX.Element =
      <Label styles={getTopLabelStyles()}>
        {TranscriptionStrings.ConfirmNewTranscriptionDialogTitle}
      </Label>

    return <DialogWithChildren
      dialogContentProps={getDialogContentProps(confirmNewTranscriptDialogTitle)}
      modalProps={getModalProps()}
      hidden={!this.state.showStartOverConfirmDialog}
      styles={styles}>
      <Label styles={AppLayoutSettings.AppCommonStyles.getPageTopLabelStyles()}>{ this.getConfirmNewTranscriptDialogMessage() }</Label>
      <DialogFooterWithChildren>
        <AppButton
          type={'AppButtonProps'}
          id="OkNewTranscriptConfirmed"
          styles={getSecondaryButtonOverrideStyles()}
          text={TranscriptionStrings.OKLabel}
          onExecute={this._onStartOverConfirmed}
        />
        <AppButton
          type={'AppButtonProps'}
          id="NewTranscriptCanceled"
          styles={getAppButtonStyles()}
          text={TranscriptionStrings.CancelButtonName}
          onExecute={this._onStartOverCanceled}
        />
      </DialogFooterWithChildren>
    </DialogWithChildren>;
  }

  private shouldScrollToHighlightedIndex() {
    // return true if audio is playing and no card is being edited (editingIndex == -1)
    return this.state.isAudioPlaying && this.state.editingIndex == INDEX_NONE;
  }

  private _onStoreChanged = (): void => {
    if (this._isMounted) {
      const prevTranscriptionManager = this.state.transcriptionManager;
      this.setState(transcribingStore.getState(), () => {
        if (prevTranscriptionManager != this.state.transcriptionManager) {
          this.LogTranscriptDurationData();
        }
      });
    }
  };

  private _notifyCurrentState = (): void => {
    if (this._isMounted && !isNullOrUndefined(this.state.sourceFile) && !isNullOrUndefined(this.audioPlayerRef.current)) {
      BroadcastService.GetInstance().BroadcastEvent(TranscriptionNotifyEvent.TRANSCRIPTION_PLAYBACK_MODE_ON, this.CreatePlaybackPayload());
    } else {
      BroadcastService.GetInstance().BroadcastEvent(TranscriptionNotifyEvent.TRANSCRIPTION_PLAYBACK_MODE_CLOSED);
    }
  }

  /*
   * Callback received when Add To document is finished by the client.
   */
  private onAddToDocumentFinished = (result: AddToDocumentResult, error: Error | null = null): void => {
    if (result == AddToDocumentResult.TIMED_OUT) {
      LogError(LogEventNames.Warning, "AddToDocument request is timed out after waiting for " + MAX_WAIT_TIME_FOR_ADD_TO_DOCUMENT_REQUEST);
    } else if (result == AddToDocumentResult.FAILURE) {
      LogError(LogEventNames.Warning, "AddToDocument request is failed with error " + ErrorStringFromException(error));
    }
    SetAddToDocumentInProgressStatus(false /* isAddToDocumentInProgress */);
  };

  private _onAddTranscriptionToDocumentClicked = async (addOption: AddToDocumentMenuOption) => {
    LogEvents.Transcript.IncrementCount(TranscriptKeys.TranscriptInsertedFull);
    const addToDocCallbackSupported = (HostAppUtil.GetHostAppName() === HostAppName.WORD);
    if (addToDocCallbackSupported) {
      SetAddToDocumentInProgressStatus(true /* isAddToDocumentInProgress */);
    }
    await AddTranscriptionToDocument(this.state.transcriptionManager!, this.props.history, this.state.sourceFile, addOption,
      addToDocCallbackSupported ? this.onAddToDocumentFinished : undefined);
  };

  private _onAddTranscriptionSegmentToDocument = (id: number): void => {
    LogEvents.Transcript.IncrementCount(TranscriptKeys.TranscriptInsertedSection);
    AddTranscriptionSegmentToDocument(this.state.transcriptionManager!, id, this.props.history);
  };

  private _onTimestampClicked = (time: number): void => {
    LogEvents.Transcript.IncrementCount(TranscriptKeys.TranscriptTimestampClicks);
    if (!this.audioPlayerRef.current) {
      return;
    }

    LogEvents.Transcript.BeginDurationMeasure(DurationMeasureType.Accumulated, TranscriptKeys.PlayDurationAcrossSessionSecs);

    this.audioPlayerRef.current.seekTime(time);
    SetAudioPlaying(true);
  };

  private _onTranscriptionDataChanged = async (data: AppTranscriptCardChangeParameter) => {
    LogEvents.Transcript.IncrementCount(TranscriptKeys.TranscriptEditSaved);
    LogEvents.Transcript.BeginDurationMeasure(DurationMeasureType.Slowest, TranscriptKeys.TranscriptEditSaveDurationSlowest)
    await TranscriptionDataModified(this.state.transcriptionManager!, data, this.props.history);
    LogEvents.Transcript.EndDurationMeasure(TranscriptKeys.TranscriptEditSaveDurationSlowest);
  }

  private _onFocusChanged = (index: number) => {
    if (dispatcher.isDispatching()) {
      return;
    }
    SetFocusedIndex(index);
  };

  private _onEditingClicked = (index: number) => {
    LogEvents.Transcript.IncrementCount(TranscriptKeys.TranscriptEditStarted);
    SetEditingMode(index);
  };

  private _onEditingModeExit = () => {
    LogEvents.Transcript.IncrementCount(TranscriptKeys.TranscriptEditClosed);
    SetEditingMode();
  };

  private _setHighlightedCard = (audioPosition: number): void => {
    let highlightIndex = -1;
    this.state.transcriptionData.map((transcriptionData, index) => {
      if (transcriptionData.timestamp > audioPosition) {
        return;
      }

      highlightIndex = index;
    });

    SetHighlightedIndex(highlightIndex);
  };

  private _onStartOverClicked = () => {
    LogEvents.StartOver.Start([{ key: StartOverKeys.Reason, value: "User" }]);
    SetShowStartOverConfirmDialog(true);
  };

  private _onStartOverConfirmed = async () => {
    SetShowStartOverConfirmDialog(false);
    await StartOverClicked(this.props.history);
    LogEvents.StartOver.End([{ key: StartOverKeys.UserResult, value: "confirmed" }]);
  }

  private _onStartOverCanceled = () => {
    SetShowStartOverConfirmDialog(false);
    LogEvents.StartOver.End([{ key: StartOverKeys.UserResult, value: "canceled" }]);
  }

  private _onAudioPlayerUserInteraction = (feature: AppAudioPlayerFeatureName, timestamp: number, playbackSpeed: number): void => {
    LogEvents.Transcript.IncrementCount(this.TranscriptKeyFromAppAudioPlayerFeatureName(feature));
    if (feature == AppAudioPlayerFeatureName.PlayAudio) {
      LogEvents.Transcript.BeginDurationMeasure(DurationMeasureType.Accumulated, TranscriptKeys.PlayDurationAcrossSessionSecs);
      SetAudioPlaying(true);
      BroadcastService.GetInstance().BroadcastEvent(TranscriptionNotifyEvent.AUDIO_PLAY, this.CreatePlaybackPayload(TranscriptionNotifyEvent.AUDIO_PLAY, timestamp, playbackSpeed));
    } else if (feature == AppAudioPlayerFeatureName.PauseAudio) {
      LogEvents.Transcript.EndDurationMeasure(TranscriptKeys.PlayDurationAcrossSessionSecs);
      SetAudioPlaying(false);
      BroadcastService.GetInstance().BroadcastEvent(TranscriptionNotifyEvent.AUDIO_PAUSE, this.CreatePlaybackPayload(TranscriptionNotifyEvent.AUDIO_PAUSE, timestamp, playbackSpeed));
    }
  };

  private _onAudioControlLoaded = (duration: number): void => {
    LogEvents.Transcript.AddData([{ key: TranscriptKeys.AudioLengthSeconds, value: duration }]);
    this.audioDuration = duration;
    if (this.state.transcribingEntryPoints == TranscribingEntryPoints.FILE_UPLOAD) {
      LogEvents.FileUpload.AddData([{ key: FileUploadKeys.AudioLengthSeconds, value: duration}]);
      LogEvents.FileUpload.End();
    }
    this.LogTranscriptDurationData();
  }

  private LogTranscriptDurationData() {
    if (this.audioDuration == null || this.state.transcriptionManager == null) {
      return;
    }

    const transcription = this.state.transcriptionManager.GetTranscription();

    if (transcription.transcriptSegments.length == 0)
      return;

    const { lastTimestampEnd, percentTranscribed } = this.GetTranscriptDurationData(transcription, this.audioDuration);
    LogEvents.Transcript.FlushEventAdhoc([{ key: TranscriptKeys.LastTimestampEnd, value: lastTimestampEnd }, { key: TranscriptKeys.PercentTranscribed, value: percentTranscribed }]);
  }

  private TranscriptKeyFromAppAudioPlayerFeatureName(feature: AppAudioPlayerFeatureName): TranscriptKeys {
    switch (feature) {
      case AppAudioPlayerFeatureName.PauseAudio:
        return TranscriptKeys.PauseAudio;
      case AppAudioPlayerFeatureName.PlayAudio:
        return TranscriptKeys.PlayAudio;
      case AppAudioPlayerFeatureName.PlaybackSpeedChange:
        return TranscriptKeys.PlaybackSpeedChange;
      case AppAudioPlayerFeatureName.SeekBackward:
        return TranscriptKeys.SeekBackward;
      case AppAudioPlayerFeatureName.SeekForward:
        return TranscriptKeys.SeekForward;
      case AppAudioPlayerFeatureName.ShowVolume:
        return TranscriptKeys.ShowVolume;
      case AppAudioPlayerFeatureName.VolumeChange:
        return TranscriptKeys.VolumeChange;
      default:
        return TranscriptKeys.UnknownAudioFeature;
    }
  }

  private GetTranscriptDurationData(transcription: ITranscription, audioFileDurationSeconds: number): { lastTimestampEnd: number, percentTranscribed: number } {
    const lastTimestampEnd = transcription.transcriptSegments[transcription.transcriptSegments.length - 1].end;

    let transcriptDuration = 0;
    transcription.transcriptSegments.forEach((segment: ITranscriptionSegment) => {
      const segmentDuration = segment.end - segment.start;
      transcriptDuration += segmentDuration;
    });

    const percentTranscribed = transcriptDuration / audioFileDurationSeconds;
    return { lastTimestampEnd, percentTranscribed };
  }

  private CreatePlaybackPayload(audioPlayerState: number = (this.state.isAudioPlaying ? TranscriptionNotifyEvent.AUDIO_PLAY : TranscriptionNotifyEvent.AUDIO_PAUSE),
    timestamp: number = this.audioPlayerRef.current!.getCurrentAudioTime(),
    playbackSpeed: number = this.audioPlayerRef.current!.getPlayBackSpeed()): PlaybackPayload {
    return {
      audioPlayerState: audioPlayerState,
      timestamp: timestamp,
      playbackSpeed: playbackSpeed
    }
  }

  private getAddTranscriptionButtonLabel() : string {
    const appName = HostAppUtil.GetHostAppName();
    switch (appName) {
      case HostAppName.WORD :
        return TranscriptionStrings.AddTranscriptionToDocumentButtonLabel;
      case HostAppName.ONENOTE :
        return TranscriptionStrings.AddTranscriptionToOneNotePageButtonLabel;
    }
    return TranscriptionStrings.AddTranscriptionToDocumentButtonLabel;
  }

  onInternetConnectionStatusChanged(state: InternetState): void {
    if (!this._isMounted) {
      return;
    }
    switch(state) {
      case InternetState.INTERNET_CONNECTED: {
        if (isNullOrUndefined(this.state.sourceFile)) {
          const transcriptionSourceFileProvider: ITranscriptionSourceFileProvider = CreateTranscriptionSourceFileProvider(TranscriptionManagerFactory.GetInstance().GetStoredTranscription(), this.context!);
          transcriptionSourceFileProvider.GetSourceFile().then((sourceFile) => {
            dispatcher.dispatch({
              type: TranscribingActionTypes.SetSourceFileAction,
              payload: {
                sourceFile: sourceFile,
                isNewTranscript: false
              }
            });
          });
        }  else {
          this.forceUpdate();
        }
      }
      break;
      case InternetState.INTERNET_DISCONNECTED:
        this.forceUpdate();
        break;
    }
  }
}